import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import type { OnChangeEventContext, OnInputChangeEventContext } from '../../shared/input/Dropdown'

import StudentSearchAutoComplete from './StudentSearchAutoComplete'
import { SearchHistory, SearchStudent } from '../../../types'
import {
  StudentProfile,
  StudentProfileFragment,
  useSearchStudentsQuery,
  useStudentUserProfileMutation
} from '../../../generated/graphql'
import useNotification from '../../../lib/hooks/useNotification'
import useMixpanelEvent from '../../../lib/hooks/useMixpanelEvent'
import useAdvisorOwnRecordGuard from '../../../lib/hooks/useAdvisorOwnRecordGuard'

/** Note: this is duplicated in scheduler monolith. */
export const StudentSearchWrapper = () => {
  const history = useHistory<SearchHistory>()
  const { addMixpanelEvent } = useMixpanelEvent()
  const { showErrorNotif } = useNotification()
  const { isOwnRecord, isOwnSisId } = useAdvisorOwnRecordGuard()
  const [searchText, setSearchText] = useState('')
  const [prevSearches, setPrevSearches] = useState<Array<SearchStudent>>([])
  const [students, setStudents] = useState<StudentProfileFragment[]>([])
  const [upsertStudentUserProfile] = useStudentUserProfileMutation({
    onCompleted: ({ studentUserProfile }) => {
      const { appUserId } = studentUserProfile
      // Authoritative backstop on the server-resolved appUserId. NOTE: this only
      // blocks navigation — the upsert has already run server-side, so the
      // pre-mutation check in handleChange is what actually prevents the write.
      if (isOwnRecord(appUserId)) {
        showErrorNotif("You can't open your own student record in advising view")
        return
      }
      history.push(`/students/${appUserId}/plans`)
    }
  })

  const { data } = useSearchStudentsQuery({
    variables: {
      text: searchText
    },
    // skip query if text is empty
    skip: !searchText,
    context: { useApolloNetworkStatus: false },
    onError: () => showErrorNotif('Could not search students')
  })

  useEffect(() => {
    if (data) {
      setStudents(data.searchStudents)
    }
  }, [data])

  const handleInputChange = (e: OnInputChangeEventContext) => {
    setSearchText(e.value)
  }
  const handleChange = (e: OnChangeEventContext) => {
    setSearchText('')
    addMixpanelEvent('Searched for a student')
    const studentProfile = e.value as StudentProfile

    // Dual-role users may not open their own student record while advising
    // (unless the institution enables advisors viewing their own plans).
    // Guard here, before the upsert mutation runs, so selecting your own record
    // neither writes a profile nor adds you to recent searches. Matches the
    // SIS externalId against both token identifier spaces (studentId /
    // externalId) — see useAdvisorOwnRecordGuard.
    if (isOwnSisId(studentProfile.externalId)) {
      showErrorNotif("You can't open your own student record in advising view")
      return
    }

    const searchedStudent: SearchStudent = { ...studentProfile, previouslySearched: true }
    let newSearches = [searchedStudent]

    // Don't add them to the list again if they're already in there
    if (JSON.stringify(prevSearches).indexOf(JSON.stringify(searchedStudent)) < 0) {
      if (prevSearches.length === 5) {
        newSearches = [searchedStudent, ...prevSearches.slice(0, prevSearches.length - 1)]
      } else if (prevSearches.length) {
        newSearches = [searchedStudent, ...prevSearches]
      }

      setPrevSearches(newSearches)
    }

    upsertStudentUserProfile({
      variables: {
        input: {
          displayName: studentProfile.preferredName,
          firstName: studentProfile.firstName,
          lastName: studentProfile.lastName,
          studentId: studentProfile.externalId,
          email: studentProfile.email
        }
      }
    })
  }

  const displayedStudents = searchText ? students : prevSearches

  return (
    <StudentSearchAutoComplete
      id="student-autocomplete"
      hasPreviousSearches={!!prevSearches.length}
      onChange={handleChange}
      onInputChange={handleInputChange}
      searchText={searchText}
      studentProfiles={displayedStudents}
    />
  )
}

export default React.memo(StudentSearchWrapper)
